import { type ReactNode, createContext, useContext } from "react";

/**
 * Context that carries a page-level refresh signal.
 *
 * ViewContainerRenderer increments the signal after successful operations;
 * embedded components (TableRenderer, LinkRenderer, etc.) watch it and
 * re-dispatch their own onInit / refresh logic when it changes.
 *
 * The initial value is 0 (no refresh requested). A value > 0 means a
 * refresh has been requested that many times since mount.
 */
const RefreshSignalContext = createContext<number>(0);

/**
 * Read the current refresh signal value.
 * Returns 0 when used outside a RefreshSignalProvider.
 */
export function useRefreshSignal(): number {
	return useContext(RefreshSignalContext);
}

export interface RefreshSignalProviderProps {
	children: ReactNode;
	/** Current refresh signal value (managed by parent container). */
	signal: number;
}

/**
 * Provides a refresh signal to descendant components.
 * The parent container owns the state and increments `signal` when a
 * page-wide refresh is needed.
 */
export function RefreshSignalProvider({ children, signal }: RefreshSignalProviderProps) {
	return <RefreshSignalContext.Provider value={signal}>{children}</RefreshSignalContext.Provider>;
}
